"use client";

import { useContext, useState, useEffect } from "react";
import { usePathname, useRouter } from "next/navigation";
import { useTranslation } from "react-i18next";

import { Wrapper } from "@/components/Wrapper";
import { ModalQRPreview } from "@/components/ModalQRPreview";
import { StepperFlowActions } from "@/components/StepperFlowActions";

import { QREditorStepper } from "@/containers/QREditor/QREditorStepperContainer/QREditorStepper";

import { AmplitudeContext } from "@/contexts/AmplitudeContext/AmplitudeContext";
import { QREditorContext } from "@/contexts/QREditorContext";
import { GlobalContext } from "@/contexts/GlobalContext";
import { UserContext } from "@/contexts/UserContext";

import { useQRInitState } from "@/hooks/qr";
import { useToast } from "@/hooks/ui/useToast";
import { useQR, useQRStyle } from "@/hooks/qr/useQR";
import { useWindowSize } from "@/hooks/browser/useWindowsSize";

import { getTokenData } from "@/services/CookiesService";
import { amplitudeLogoutUser } from "@/services/AmplitudeService/AmplitudeService";

import { clearPendingActionCookie } from "@/actions/clearPendingActionCookie";
import { setPendingActionCookie } from "@/actions/setPendingActionCookie";
import { getPendingActionCookie } from "@/actions/getPendingActionCookie";

import { ActionTypes } from "@/internals/constants/actionTypes";

import { omittedValues, getQRCodeUrl } from "@/utils/qrEditorHelper";
import {
  QR_PAGE_INDEX,
  getPageIndexFromUrl,
  handleQRFooterDirectNavigation,
  handleBackButton,
  handleNextButton,
} from "@/utils/qrEditorNavigatorHelper";
import { ApiErrorMessageBuilder } from "@/utils/apiErrorHandler";
import { paths } from "@/utils/paths";

import { initialStepperButtons } from "@/internals/constants/stepperButtons";
import { API_ERROR_FALLBACKS } from "@/internals/constants/errorCodes";
import { PAGES } from "@/internals/constants/pages";

import { OnNextButtonAction, OnPreviewModalAction } from "@/types/pendingActions";
import { QREditorContextProps } from "@/contexts/QREditorContext/types";
import { QRFooterProps } from "./types";

import "./styles.scss";

export const QREditorFooter = ({ data, defaultPreviewTitle }: QRFooterProps) => {
  const { t } = useTranslation(PAGES.QR_EDITOR_LAYOUT);
  const screenSize = useWindowSize();
  const router = useRouter();
  const pathname = usePathname();
  const { toast } = useToast();
  const globalContext = useContext(GlobalContext);
  const userContext = useContext(UserContext);
  const { setSource } = useContext(AmplitudeContext);
  const qrEditorContext = useContext<QREditorContextProps<any>>(QREditorContext);
  const { requestQR, isLoading, newData } = useQR();
  const isQRInit = useQRInitState(qrEditorContext);

  const { getRawData, updateQRCode } = useQRStyle();
  const [previewType, setPreviewType] = useState<"qr" | "data" | undefined>(
    qrEditorContext?.qrData?.currentPageIndex === QR_PAGE_INDEX.DESIGN_QR ? "qr" : "data"
  );
  const [isQRActive, setisQRActive] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [qrRaw, setQrRaw] = useState(undefined);

  const stepperButtons = initialStepperButtons(data.stepperButtons);
  const currentPageIndex =
    qrEditorContext?.qrData?.currentPageIndex ?? QR_PAGE_INDEX.SELECT_QR_TYPE;
  const isFirstStep = currentPageIndex === QR_PAGE_INDEX.SELECT_QR_TYPE;

  const buttonLabelPrev = isFirstStep
    ? data.helperButtons.cancelButton.label
    : data.helperButtons.backButton.label;

  const buttonLabelNext =
    currentPageIndex === QR_PAGE_INDEX.DESIGN_QR
      ? data.helperButtons.finishButton.label
      : data.helperButtons.nextButton.label;

  const onPreviewQr = () => {
    updateQRCode({
      qrStyle: qrEditorContext.qrData.qrStyle,
      qrUrl: getQRCodeUrl(qrEditorContext.qrData, true),
    });

    if (requestQR) {
      qrEditorContext?.setIsQrLoading(true);
      requestQR()
        .then(async (data: any) => {
          if (data !== undefined) {
            qrEditorContext?.setQrData(
              {
                ...qrEditorContext.qrData,
                qrCodeId: data?.id || qrEditorContext.qrData.qrCodeId,
                qrCodeReference: data?.reference || qrEditorContext.qrData.qrCodeReference,
              },
              [...omittedValues(qrEditorContext?.qrData.data)]
            );
            const raw = await getRawData();

            setIsModalOpen(true);
            setQrRaw(raw);
            qrEditorContext?.setIsQrLoading(false);
          }
        })
        .catch((e) => {
          const errorMessage = ApiErrorMessageBuilder.buildToastMessage(e, {
            showDetails: true,
            includeCode: true,
            includeTimestamp: true,
            fallbackMessage: API_ERROR_FALLBACKS.QR_EDITOR.PREVIEW,
          });
          toast({ message: errorMessage, type: "error", marginBottom: 80 });
        });
    }
  };

  const isNextButtonVisible = () => {
    return (
      currentPageIndex > QR_PAGE_INDEX.SELECT_QR_TYPE && currentPageIndex < QR_PAGE_INDEX.REGISTER
    );
  };

  const handlePreviewModal = () => {
    if (previewType === "qr") {
      onPreviewQr();
    } else {
      const tokenData = getTokenData();
      if (tokenData?.isTokenExpired) {
        const qrType = qrEditorContext?.qrData.type;

        const isDesignPage = currentPageIndex === QR_PAGE_INDEX.DESIGN_QR ? "design" : undefined;

        const pendingAction: OnPreviewModalAction = {
          type: ActionTypes.PREVIEW_MODAL,
          redirectTo: paths.qrEditor(qrType, isDesignPage),
        };
        setPendingActionCookie(pendingAction);
        router.push(paths.signup());
      } else {
        setIsModalOpen(true);
      }
    }
  };

  const onNextButton = () => {
    const tokenData = getTokenData();

    if (tokenData?.isTokenExpired) {
      const qrType = qrEditorContext?.qrData.type;

      const isDesignPage = currentPageIndex === QR_PAGE_INDEX.DESIGN_QR ? "design" : undefined;

      const pendingAction: OnNextButtonAction = {
        type: ActionTypes.ON_NEXT_BUTTON,
        redirectTo: paths.qrEditor(qrType, isDesignPage),
      };
      setPendingActionCookie(pendingAction);
      amplitudeLogoutUser();
      router.push(paths.login());
    } else {
      handleNextButton(
        globalContext,
        qrEditorContext,
        userContext,
        requestQR,
        toast,
        true,
        router,
        setSource,
        t
      );
    }
  };

  useEffect(() => {
    const parsedPendingAction = getPendingActionCookie();

    parsedPendingAction.then((pendingAction) => {
      if (pendingAction) {
        if (pendingAction.type === ActionTypes.ON_NEXT_BUTTON) {
          onNextButton();
          clearPendingActionCookie();
        } else if (pendingAction.type === ActionTypes.PREVIEW_MODAL) {
          handlePreviewModal();
          clearPendingActionCookie();
        }
      }
    });
  }, []);

  useEffect(() => {
    const pageIndexFromUrl = getPageIndexFromUrl(pathname);
    const contextPageIndex = qrEditorContext?.qrData?.currentPageIndex;

    const timer = setTimeout(() => {
      if (pageIndexFromUrl !== qrEditorContext?.qrData?.currentPageIndex) {
        qrEditorContext.setQrData({
          ...qrEditorContext.qrData,
          currentPageIndex: pageIndexFromUrl,
        });
      }
    }, 50);

    handleQRFooterDirectNavigation(pageIndexFromUrl, contextPageIndex, qrEditorContext, router);
    return () => {
      clearTimeout(timer);
    };
  }, [pathname, globalContext]);

  useEffect(() => {
    const activeQR = qrEditorContext?.qrData.isValidForm && !qrEditorContext?.validationError;
    setisQRActive(activeQR);
  }, [qrEditorContext]);

  useEffect(() => {
    if (newData) {
      qrEditorContext.setQrData(
        {
          ...qrEditorContext.qrData,
          data: newData.data,
          qrStyle: newData.qrStyle,
        },
        [...omittedValues(qrEditorContext?.qrData.data)]
      );
    }
  }, [newData]);

  return (
    <footer
      data-testid="qr-footer"
      className="qr-footer"
    >
      <Wrapper customClass="qr-footer__wrapper">
        <StepperFlowActions
          isFirstStep={isFirstStep}
          backAriaLabel={buttonLabelPrev}
          showPreview={currentPageIndex > QR_PAGE_INDEX.SELECT_QR_TYPE && !screenSize?.isDesktop}
          previewAriaLabel={t("preview_button_label")}
          nextLabel={buttonLabelNext}
          onBack={() => handleBackButton(qrEditorContext, router)}
          onPreview={handlePreviewModal}
          onNext={onNextButton}
          showNext={isNextButtonVisible()}
          isNextLoading={isLoading && !qrEditorContext?.isQrLoading}
          isNextDisabled={!qrEditorContext?.qrData.type}
          stepper={
            screenSize?.isDesktop ? (
              <QREditorStepper
                steps={stepperButtons}
                pathname={pathname}
                variant="labeled"
              />
            ) : undefined
          }
        />

        {isModalOpen && (
          <ModalQRPreview
            isModalOpen={isModalOpen}
            setIsModalOpen={setIsModalOpen}
            previewType={previewType}
            previewButtonsTranslations={data.previewButtonsTranslations}
            data={{
              qrData: qrEditorContext.qrData as any,
              previewTranslations: data.previewTranslations,
              previewType,
              setPreviewType,
              isQRActive,
              raw: qrRaw,
              handlePreviewModal,
            }}
            setQrRaw={setQrRaw}
            isQRInit={isQRInit}
            modalData={data.modalData}
            defaultPreviewTitle={defaultPreviewTitle}
          />
        )}
      </Wrapper>
    </footer>
  );
};
